import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from 'react'
import { useSearchParams } from 'react-router-dom'
import { oauth, type AppInfo } from '../api/client'

interface AppContextValue {
  appInfo: AppInfo | null
  clientId: string | null
  returnTo: string | null
  loading: boolean
}

const AppContext = createContext<AppContextValue>({
  appInfo: null,
  clientId: null,
  returnTo: null,
  loading: false,
})

const STORAGE_KEY = 'u_auth_app_context'

interface StoredContext {
  appInfo: AppInfo
  clientId: string
  returnTo: string
  timestamp: number
}

/**
 * Parse OAuth params from a return_to URL
 */
function parseOAuthParams(returnTo: string | null): { clientId: string | null } {
  if (!returnTo) return { clientId: null }

  try {
    const url = new URL(returnTo, window.location.origin)
    // Check if this is an authorize URL
    if (url.pathname.includes('/authorize')) {
      return { clientId: url.searchParams.get('client_id') }
    }
  } catch {
    // Invalid URL
  }
  return { clientId: null }
}

export function AppContextProvider({ children }: { children: ReactNode }) {
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('return_to')
  const { clientId } = parseOAuthParams(returnTo)

  const [appInfo, setAppInfo] = useState<AppInfo | null>(null)
  const [loading, setLoading] = useState(false)

  // Load from sessionStorage on mount
  useEffect(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY)
    if (stored) {
      try {
        const parsed: StoredContext = JSON.parse(stored)
        // Check if context is still fresh (within 30 minutes)
        const isExpired = Date.now() - parsed.timestamp > 30 * 60 * 1000
        if (!isExpired) {
          // If current clientId matches stored, use stored data
          if (!clientId || parsed.clientId === clientId) {
            setAppInfo(parsed.appInfo)
            return
          }
        }
      } catch {
        // Invalid stored data
        sessionStorage.removeItem(STORAGE_KEY)
      }
    }

    // Fetch app info if we have a client_id
    if (clientId) {
      setLoading(true)
      oauth.getAppInfo(clientId).then((info) => {
        setAppInfo(info)
        if (info && returnTo) {
          // Store in sessionStorage for multi-page flow
          const toStore: StoredContext = {
            appInfo: info,
            clientId,
            returnTo,
            timestamp: Date.now(),
          }
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify(toStore))
        }
        setLoading(false)
      })
    }
  }, [clientId, returnTo])

  return (
    <AppContext.Provider
      value={{
        appInfo,
        clientId,
        returnTo,
        loading,
      }}
    >
      {children}
    </AppContext.Provider>
  )
}

export function useAppContext() {
  return useContext(AppContext)
}

/**
 * Clear stored app context (call after OAuth flow completes)
 */
export function clearAppContext() {
  sessionStorage.removeItem(STORAGE_KEY)
}
